import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calculator,
  CalendarDays,
  Save,
  CheckCircle2,
  AlertCircle,
  Info,
  Lock,
  RotateCcw,
} from "lucide-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import {
  heuteIso,
  datumDeutsch,
  kritischeAenderungBestaetigen,
  setzeVeranstaltungstagCache,
} from "../utils/veranstaltungstag";

const STANDARD = { max_points: 100, step: 4 };
const MAX_PUNKTE_OBERGRENZE = 1000;

// Spiegelt functions/services/punkteschema.js – dieselben Regeln, damit
// Frontend und Backend nie auseinanderlaufen.
function pruefeSchema({ max_points, step }) {
  const ganz = (w) => typeof w === "number" && Number.isInteger(w);
  const fehler = {};
  if (!ganz(max_points) || max_points < 1 || max_points > MAX_PUNKTE_OBERGRENZE) {
    fehler.max_points = `Ganze Zahl von 1 bis ${MAX_PUNKTE_OBERGRENZE}.`;
  }
  if (!ganz(step) || step < 1) {
    fehler.step = "Ganze Zahl ab 1.";
  } else if (ganz(max_points) && step > max_points) {
    fehler.step = "Darf nicht größer sein als die Punkte für Platz 1.";
  }
  return fehler;
}

const punkteFuerPlatz = (platz, s) => Math.max(0, s.max_points - (platz - 1) * s.step);

export default function Wertung() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [ladeFehler, setLadeFehler] = useState(null);
  const [veroeffentlicht, setVeroeffentlicht] = useState(false);

  const [schema, setSchema] = useState(STANDARD);
  const [schemaGespeichert, setSchemaGespeichert] = useState(STANDARD);
  const [schemaSpeichert, setSchemaSpeichert] = useState(false);
  const [schemaMeldung, setSchemaMeldung] = useState(null);

  const [datum, setDatum] = useState("");
  const [datumGespeichert, setDatumGespeichert] = useState("");
  const [datumSpeichert, setDatumSpeichert] = useState(false);
  const [datumMeldung, setDatumMeldung] = useState(null);

  // Teams je Kategorie – fuer den Hinweis, ob die Plaetze bis zur 0 reichen.
  const [teamsJeKategorie, setTeamsJeKategorie] = useState(null);
  useEffect(() => {
    fetchWithAuth(`${BACKEND_URL}/teams`, navigate)
      .then((teams) => {
        const zaehler = { Erwachsene: 0, Kinder: 0 };
        for (const t of teams || []) if (t.event_type in zaehler) zaehler[t.event_type]++;
        setTeamsJeKategorie(zaehler);
      })
      .catch(() => setTeamsJeKategorie(null)); // nur ein Hinweis – ohne geht es auch
  }, [BACKEND_URL, navigate]);

  const laden = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`${BACKEND_URL}/wertung`, navigate);
      setSchema(data.punkteschema);
      setSchemaGespeichert(data.punkteschema);
      setDatum(data.veranstaltungstag || "");
      setDatumGespeichert(data.veranstaltungstag || "");
      setVeroeffentlicht(!!data.results_published);
      setzeVeranstaltungstagCache(data.veranstaltungstag);
      setLadeFehler(null);
    } catch (err) {
      console.error(err);
      setLadeFehler(`Ladefehler: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [BACKEND_URL, navigate]);

  useEffect(() => {
    laden();
  }, [laden]);

  const fehler = useMemo(() => pruefeSchema(schema), [schema]);
  const schemaGueltig = Object.keys(fehler).length === 0;
  const schemaGeaendert =
    schema.max_points !== schemaGespeichert.max_points || schema.step !== schemaGespeichert.step;

  // Vorschau: Plaetze bis einschliesslich des ersten mit 0 Punkten, hoechstens 12.
  const vorschau = useMemo(() => {
    if (!schemaGueltig) return { plaetze: [], nullAb: null };
    const nullAb = Math.floor((schema.max_points - 1) / schema.step) + 2;
    const plaetze = [];
    for (let p = 1; p < Math.min(nullAb, 13); p++) plaetze.push({ platz: p, punkte: punkteFuerPlatz(p, schema) });
    return { plaetze, nullAb, gekuerzt: nullAb > 13 };
  }, [schema, schemaGueltig]);

  const aendereSchema = (feld, wert) =>
    setSchema((s) => ({ ...s, [feld]: wert === "" ? null : Number(wert) }));

  const speichereSchema = async () => {
    if (!schemaGueltig || veroeffentlicht) return;
    const ok = await kritischeAenderungBestaetigen(
      `Punkteschema ändern auf ${schema.max_points} Punkte für Platz 1, ` +
        `${schema.step} Punkte Abstand. Alle Stationspunkte werden neu berechnet.`
    );
    if (!ok) return;

    setSchemaSpeichert(true);
    setSchemaMeldung(null);
    try {
      const data = await fetchWithAuth(`${BACKEND_URL}/wertung/punkte`, navigate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ max_points: schema.max_points, step: schema.step }),
      });
      setSchemaGespeichert(data.punkteschema);
      setSchemaMeldung({ type: "success", text: "Punkteschema gespeichert, Ergebnisse neu berechnet." });
      setTimeout(() => setSchemaMeldung(null), 4000);
    } catch (err) {
      console.error(err);
      // 409 = zwischenzeitlich freigegeben: Status neu laden, damit die Sperre sichtbar wird.
      if (err.status === 409) setVeroeffentlicht(true);
      setSchemaMeldung({ type: "error", text: `Speicherfehler: ${err.message}` });
    } finally {
      setSchemaSpeichert(false);
    }
  };

  const speichereDatum = async (neu) => {
    setDatumSpeichert(true);
    setDatumMeldung(null);
    try {
      const data = await fetchWithAuth(`${BACKEND_URL}/wertung/veranstaltungstag`, navigate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ datum: neu || null }),
      });
      setDatum(data.veranstaltungstag || "");
      setDatumGespeichert(data.veranstaltungstag || "");
      setzeVeranstaltungstagCache(data.veranstaltungstag);
      setDatumMeldung({ type: "success", text: neu ? "Veranstaltungstag gespeichert." : "Veranstaltungstag entfernt." });
      setTimeout(() => setDatumMeldung(null), 3000);
    } catch (err) {
      console.error(err);
      setDatumMeldung({ type: "error", text: `Speicherfehler: ${err.message}` });
    } finally {
      setDatumSpeichert(false);
    }
  };

  const heute = heuteIso();
  const datumStatus = !datumGespeichert
    ? null
    : datumGespeichert === heute
      ? { text: "Heute ist Veranstaltungstag", ton: "warn" }
      : datumGespeichert < heute
        ? { text: "Veranstaltungstag ist vorbei", ton: "warn" }
        : { text: `Noch ${tageBis(heute, datumGespeichert)} Tag(e)`, ton: "ok" };

  return (
    <div className="p-4 sm:p-6 bg-secondary-950 min-h-screen">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <Calculator size={22} />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">Wertung</h1>
              <p className="text-sm text-secondary-100">Veranstaltungstag und Punkte je Stationsplatz</p>
            </div>
          </div>
        </header>

        {isLoading ? (
          <div className="py-16 text-center text-secondary-200">Lade Daten…</div>
        ) : ladeFehler ? (
          <Meldung meldung={{ type: "error", text: ladeFehler }} />
        ) : (
          <>
            {/* === Veranstaltungstag === */}
            <section className="rounded-xl border border-secondary-800 bg-secondary-900 p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <CalendarDays size={18} className="text-primary-500" />
                <h2 className="font-semibold text-white">Veranstaltungstag</h2>
                {datumStatus && (
                  <span
                    className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      datumStatus.ton === "warn" ? "bg-amber-500/15 text-amber-300" : "bg-green-500/15 text-green-300"
                    }`}
                  >
                    {datumStatus.text}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-secondary-200">
                Ab diesem Tag fragt die App vor kritischen Änderungen (Punkteschema, Altersbonus,
                Stationen und Teams bearbeiten oder löschen, Import, Resets) noch einmal nach.
              </p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-secondary-200">Datum</span>
                  <input
                    type="date"
                    value={datum}
                    onChange={(e) => setDatum(e.target.value)}
                    className="rounded-lg border border-secondary-700 bg-secondary-800 px-3 py-2 text-white outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 [color-scheme:dark]"
                  />
                </label>
                <button
                  onClick={() => speichereDatum(datum)}
                  disabled={datumSpeichert || datum === datumGespeichert}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-5 py-2.5 font-semibold text-secondary-950 shadow transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-700 disabled:text-secondary-300"
                >
                  <Save size={18} /> {datumSpeichert ? "Speichern…" : "Speichern"}
                </button>
                {datumGespeichert && (
                  <button
                    onClick={() => speichereDatum(null)}
                    disabled={datumSpeichert}
                    className="rounded-lg px-4 py-2.5 text-sm text-secondary-200 transition-colors hover:bg-secondary-800 hover:text-white"
                  >
                    Entfernen
                  </button>
                )}
              </div>
              {datumGespeichert && (
                <p className="mt-2 text-xs text-secondary-300">Gespeichert: {datumDeutsch(datumGespeichert)}</p>
              )}
              <Meldung meldung={datumMeldung} />
            </section>

            {/* === Punkteschema === */}
            <section className="rounded-xl border border-secondary-800 bg-secondary-900 p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <Calculator size={18} className="text-primary-500" />
                <h2 className="font-semibold text-white">Punkte je Stationsplatz</h2>
              </div>
              <div className="mt-2 flex items-start gap-2 text-sm text-secondary-200">
                <Info size={16} className="mt-0.5 shrink-0 text-primary-500" />
                <p>
                  Platz 1 an einer Station bekommt die volle Punktzahl, jeder weitere Platz den Abstand
                  weniger – nie unter 0. Bei Gleichstand bekommen alle dieselben Punkte, danach wird
                  entsprechend übersprungen. Der Altersbonus bleibt davon unberührt, sein Gewicht
                  verschiebt sich aber mit der Punktzahl.
                </p>
              </div>

              {veroeffentlicht && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-500/15 p-3 text-sm text-amber-200">
                  <Lock size={16} className="mt-0.5 shrink-0" />
                  <p>
                    Die Ergebnisse sind freigegeben – das Punkteschema ist gesperrt. Zum Ändern zuerst
                    die Freigabe unter{" "}
                    <Link to="/freischalten" className="underline hover:text-white">Freischalten</Link>{" "}
                    zurücknehmen.
                  </p>
                </div>
              )}

              <fieldset disabled={veroeffentlicht} className="mt-4 disabled:opacity-60">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <Zahlenfeld
                    label="Punkte für Platz 1"
                    unit="Punkte"
                    value={schema.max_points}
                    onChange={(v) => aendereSchema("max_points", v)}
                    fehler={fehler.max_points}
                  />
                  <Zahlenfeld
                    label="Abstand je Platz"
                    unit="Punkte"
                    value={schema.step}
                    onChange={(v) => aendereSchema("step", v)}
                    fehler={fehler.step}
                  />
                </div>

                {schemaGueltig && (
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-medium text-secondary-200">Vorschau</p>
                    <div className="flex flex-wrap gap-1.5">
                      {vorschau.plaetze.map(({ platz, punkte }) => (
                        <span key={platz} className="rounded-md bg-secondary-800 px-2 py-1 text-xs text-secondary-100">
                          {platz}. <span className="font-semibold text-white">{punkte}</span>
                        </span>
                      ))}
                      {vorschau.gekuerzt && <span className="px-1 py-1 text-xs text-secondary-300">…</span>}
                    </div>
                    <p className="mt-2 text-xs text-secondary-300">
                      Ab Platz {vorschau.nullAb} gibt es 0 Punkte.
                    </p>
                    {/* Mehr Teams als Plaetze mit Punkten: die letzten an einer
                        Station bekommen alle 0 und sind dort nicht mehr
                        unterscheidbar. Frueher stand nur "ab Platz X". */}
                    {teamsJeKategorie &&
                      Object.entries(teamsJeKategorie)
                        // Erst ab zwei Teams mit 0 Punkten geht die Unterscheidung verloren.
                        .filter(([, anzahl]) => anzahl > vorschau.nullAb)
                        .map(([kategorie, anzahl]) => (
                          <p key={kategorie} className="mt-1 flex items-start gap-1.5 text-xs text-amber-300">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                              {kategorie}: {anzahl} Teams – an einer Station, an der alle antreten, bekommen die
                              letzten {anzahl - vorschau.nullAb + 1} gleich 0 Punkte und werden dort nicht mehr
                              unterschieden. Mehr Punkte für Platz 1 oder ein kleinerer Abstand helfen.
                            </span>
                          </p>
                        ))}
                  </div>
                )}

                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={speichereSchema}
                    disabled={schemaSpeichert || !schemaGueltig || !schemaGeaendert}
                    className="flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-6 py-3 font-semibold text-secondary-950 shadow transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-700 disabled:text-secondary-300"
                  >
                    <Save size={18} /> {schemaSpeichert ? "Speichern…" : "Speichern & neu berechnen"}
                  </button>
                  {(schema.max_points !== STANDARD.max_points || schema.step !== STANDARD.step) && (
                    <button
                      onClick={() => setSchema(STANDARD)}
                      className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm text-secondary-200 transition-colors hover:bg-secondary-800 hover:text-white"
                    >
                      <RotateCcw size={16} /> Standard (100 / 4)
                    </button>
                  )}
                </div>
              </fieldset>
              <Meldung meldung={schemaMeldung} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function tageBis(vonIso, bisIso) {
  const ms = Date.parse(bisIso + "T00:00:00Z") - Date.parse(vonIso + "T00:00:00Z");
  return Math.round(ms / 86400000);
}

function Meldung({ meldung }) {
  if (!meldung) return null;
  return (
    <div
      className={`mt-4 flex items-center gap-2 rounded-lg p-3 text-sm font-medium ${
        meldung.type === "success" ? "bg-green-500/15 text-green-300" : "bg-red-500/15 text-red-300"
      }`}
    >
      {meldung.type === "success" ? <CheckCircle2 size={18} className="shrink-0" /> : <AlertCircle size={18} className="shrink-0" />}
      {meldung.text}
    </div>
  );
}

function Zahlenfeld({ label, unit, value, onChange, fehler }) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-xs font-medium text-secondary-200">{label}</span>
      <span
        className={`flex items-center rounded-lg border bg-secondary-800 px-3 transition-colors focus-within:ring-2 ${
          fehler
            ? "border-red-500/60 focus-within:ring-red-500/40"
            : "border-secondary-700 focus-within:border-primary-500 focus-within:ring-primary-500/40"
        }`}
      >
        <input
          type="number"
          inputMode="numeric"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          className="w-full bg-transparent py-2 font-semibold text-white outline-none"
        />
        <span className="ml-2 shrink-0 text-xs text-secondary-300">{unit}</span>
      </span>
      {fehler && (
        <span className="flex items-center gap-1.5 text-xs text-red-400">
          <AlertCircle size={13} className="shrink-0" /> {fehler}
        </span>
      )}
    </label>
  );
}
